import { Button, Checkbox, InputNumber, message } from 'antd'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import { FoodSafetySurfaceCard } from 'src/components/food-safety/FoodSafetyPageChrome'
import {
  FOOD_SAFETY_COOLING_SUGGESTED_TARGET,
  FOOD_SAFETY_SECONDARY_TEXT,
} from 'src/constants/foodSafety'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import { updateFoodSafetySettings } from 'src/utils/foodSafetyApi'
import {
  FOOD_SAFETY_COOLING_MAX_MINUTES_LIMITS,
  FOOD_SAFETY_COOLING_REMIND_AFTER_LIMITS,
  FOOD_SAFETY_COOLING_TEMP_LIMITS,
  formatCoolingDuration,
  buildFoodSafetyCoolingTargetPatch,
  buildFoodSafetyCoolingTargetResetPatch,
  coolingTargetDraftFromSettings,
  formatFoodSafetyCoolingTarget,
  validateFoodSafetyCoolingTargetDraft,
} from 'src/utils/foodSafetyCooling'

const TITLE = 'Cooling target'

const sameDraft = (a, b) =>
  Boolean(a.noTarget) === Boolean(b.noTarget) &&
  String(a.tempMax ?? '') === String(b.tempMax ?? '') &&
  String(a.maxMinutes ?? '') === String(b.maxMinutes ?? '') &&
  String(a.remindAfterMinutes ?? '') === String(b.remindAfterMinutes ?? '')

/**
 * Venue cooling target on Setup → Cooling: the final temperature and an
 * optional max cooling time. Saves only `coolingTarget` — past logs keep the
 * target they were taken under.
 */
const FoodSafetyCoolingTargetCard = ({
  venueId,
  target,
  remindAfterMinutes,
  onSaved,
}) => {
  const saved = coolingTargetDraftFromSettings(target, remindAfterMinutes)
  const [draft, setDraft] = useState(saved)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const saveLock = useFoodSafetySaveLock()
  const savedKey = JSON.stringify(saved)

  useEffect(() => {
    setDraft(JSON.parse(savedKey))
    setError(null)
  }, [savedKey])

  const suggestedLabel = formatFoodSafetyCoolingTarget(
    FOOD_SAFETY_COOLING_SUGGESTED_TARGET,
  )
  const dirty = !sameDraft(draft, saved)
  const custom = target?.source === 'venue'
  const minutesPreview =
    draft.maxMinutes !== '' && draft.maxMinutes != null
      ? formatCoolingDuration(draft.maxMinutes)
      : ''
  const patch = next => {
    setDraft(prev => ({ ...prev, ...next }))
    setError(null)
  }

  const send = async (body, successText) => {
    if (!saveLock.tryAcquire()) return
    setSaving(true)
    try {
      const data = await updateFoodSafetySettings(venueId, body)
      message.success(successText)
      onSaved?.(data)
    } catch (err) {
      setError(err?.message || 'Could not save the cooling target.')
    } finally {
      saveLock.release()
      setSaving(false)
    }
  }

  const save = () => {
    const problem = validateFoodSafetyCoolingTargetDraft(draft)
    if (problem) {
      setError(problem)
      return
    }
    send(buildFoodSafetyCoolingTargetPatch(draft), `${TITLE} saved`)
  }

  const useSuggested = () =>
    send(
      buildFoodSafetyCoolingTargetResetPatch(),
      `${TITLE} back to ${suggestedLabel}`,
    )

  return (
    <Card data-testid='fs-cooling-target'>
      <H3>{TITLE}</H3>
      <BodyText>
        Every cooling log is measured against this. A cooling item with its own
        max temp keeps it.
      </BodyText>
      <Current data-testid='fs-cooling-target-current'>
        Now: {formatFoodSafetyCoolingTarget(target)}
        {custom ? '' : ' (suggested)'}
      </Current>
      <Fields>
        <Field>
          <Label as='label' htmlFor='fs-cooling-target-temp-input'>
            Cooled to °C or below
          </Label>
          <InputNumber
            id='fs-cooling-target-temp-input'
            value={draft.noTarget ? null : draft.tempMax}
            onChange={tempMax => patch({ tempMax })}
            disabled={draft.noTarget}
            min={FOOD_SAFETY_COOLING_TEMP_LIMITS.min}
            max={FOOD_SAFETY_COOLING_TEMP_LIMITS.max}
            placeholder='None'
            data-testid='fs-cooling-target-temp'
          />
        </Field>
        <Field>
          <Label as='label' htmlFor='fs-cooling-target-minutes-input'>
            Max cooling time (minutes)
          </Label>
          <InputNumber
            id='fs-cooling-target-minutes-input'
            aria-describedby='fs-cooling-target-minutes-hint'
            value={draft.maxMinutes}
            onChange={maxMinutes => patch({ maxMinutes })}
            min={FOOD_SAFETY_COOLING_MAX_MINUTES_LIMITS.min}
            max={FOOD_SAFETY_COOLING_MAX_MINUTES_LIMITS.max}
            step={5}
            precision={0}
            placeholder='No limit'
            data-testid='fs-cooling-target-minutes'
          />
          <FieldHint
            id='fs-cooling-target-minutes-hint'
            data-testid='fs-cooling-target-minutes-hint'
          >
            {minutesPreview
              ? `Flag if it takes longer than ${minutesPreview}`
              : 'Blank — how long it took isn’t flagged'}
          </FieldHint>
        </Field>
        <Field>
          <Label as='label' htmlFor='fs-cooling-remind-after-input'>
            Check cooling after (minutes)
          </Label>
          <InputNumber
            id='fs-cooling-remind-after-input'
            aria-describedby='fs-cooling-remind-after-hint'
            value={draft.remindAfterMinutes}
            onChange={minutes => patch({ remindAfterMinutes: minutes })}
            min={FOOD_SAFETY_COOLING_REMIND_AFTER_LIMITS.min}
            max={FOOD_SAFETY_COOLING_REMIND_AFTER_LIMITS.max}
            step={5}
            precision={0}
            data-testid='fs-cooling-remind-after'
          />
          <FieldHint
            id='fs-cooling-remind-after-hint'
            data-testid='fs-cooling-remind-after-hint'
          >
            Open batches show elapsed time against this — not morning/afternoon
            slots. We&apos;ll ping once when an open batch hits this time.
            {draft.remindAfterMinutes
              ? ` Now ${formatCoolingDuration(draft.remindAfterMinutes)}.`
              : ''}
          </FieldHint>
        </Field>
      </Fields>
      <span data-testid='fs-cooling-target-none'>
        <Checkbox
          checked={draft.noTarget}
          onChange={e => patch({ noTarget: e.target.checked })}
        >
          No target — record final temps without flagging them
        </Checkbox>
      </span>
      {error ? (
        <ErrorText role='alert' data-testid='fs-cooling-target-error'>
          {error}
        </ErrorText>
      ) : null}
      <Actions>
        <Button
          type={dirty ? 'primary' : 'default'}
          disabled={!dirty}
          loading={saving}
          onClick={save}
          data-testid='fs-cooling-target-save'
        >
          Save target
        </Button>
        {custom ? (
          <Button
            type='link'
            disabled={saving}
            onClick={useSuggested}
            data-testid='fs-cooling-target-reset'
          >
            Use suggested ({suggestedLabel})
          </Button>
        ) : null}
      </Actions>
      <Hint>Changes apply to new logs. Past logs keep their target.</Hint>
    </Card>
  )
}

export default FoodSafetyCoolingTargetCard

const Card = styled(FoodSafetySurfaceCard)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const Current = styled.p`
  margin: 0;
  font-weight: 600;
`

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
`

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Label = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const FieldHint = styled.span`
  font-size: 0.75rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #c47a12;
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`
